/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Container, makeStyles, Typography } from '@material-ui/core';

import IconClose from '../../assets/images/ic-close-round.svg';
import { DCM, IMAGE, VIDEO, DOCS, reducedResourceTypes, DOCS_IMAGES, PDF, DOCX } from '../../models/Resource';
import { megaBytesToBytes, sanitizeFileName } from '../../utils/files';
import EditFields from '../common/EditFields';
import LoadingButton from '../common/LoadingButton';
import { imageFormats, videoFormats, documentFormats } from '../resources/AddResource';

const useStyles = makeStyles((theme) => {
  return {
    buttonCancel: {
      marginRight: '8px',
    },
    container: {
      display: 'block',
      margin: '0px',
      padding: '20px',
      position: 'relative',
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: '300px',
      },
    },
    containerButton: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'right',
      marginTop: '16px',
      width: '100%',
    },
    fieldContainer: {
      marginBottom: '0px',
      marginTop: '16px',
    },
    fieldLabel: {
      fontWeight: 'normal !important',
    },
    iconClose: {
      cursor: 'pointer',
      height: '24px',
      position: 'absolute',
      right: '16px',
      top: '16px',
      width: '24px',
    },
    typographyError: {
      color: 'red',
      fontSize: '12px',
      lineBreak: 'anywhere',
      lineHeight: '18px',
      marginTop: '4px',
    },
    typographyFilename: {
      color: '#999999',
      fontSize: '12px',
      lineBreak: 'anywhere',
      lineHeight: '18px',
      marginTop: '4px',
    },
    typographyTitle: {
      color: '#172B4D',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      marginBottom: '5px',
      textAlign: 'left',
    },
    uploadFileContainer: {
      marginTop: '16px',
    },
  };
});

export default ({ allowDocxFiles = false, onCrossIconClick, onUpload, dialogTitle, confirmateUpload = false }) => {
  const classes = useStyles();
  const [selectedType, setSelectedType] = useState();
  const [files, setFiles] = useState(null);
  const [unsupportedType, setUnsupportedType] = useState(false);
  const [sizeError, setSizeError] = useState(null);

  const typeFieldsRef = useRef();
  const typeFields = useMemo(() => {
    return [
      {
        containerClass: classes.fieldContainer,
        items: reducedResourceTypes,
        label: 'File Type',
        name: 'type',
        onChange: (value) => {
          if (selectedType === value.type) return;
          setUnsupportedType(false);
          setSelectedType(value.type);
          setFiles(null);
          setSizeError(null);
        },
        required: true,
        type: 'select',
      },
    ];
  }, [classes.fieldContainer, selectedType]);

  const handleClose = () => {
    if (onCrossIconClick) onCrossIconClick();
  };

  const checkFileSize = (message = 'File exceeded size', fileSize, size = 5) => {
    if (fileSize > megaBytesToBytes(size)) {
      setSizeError(message);
      setFiles(null);
      return true;
    }

    return false;
  };

  const handleUpload = () => {
    if (!onUpload) return;

    let { type } = files[0];
    let originalType = type || selectedType;

    if (selectedType === DCM) {
      onUpload({ files, originalType, type: selectedType });
      return;
    }

    const sanitizedFiles = files.map((file) => sanitizeFileName(file));

    const file = sanitizedFiles[0];
    const { name } = file;

    if (selectedType === VIDEO) {
      if (type.includes(VIDEO)) {
        if (checkFileSize('Video files should not exceed 50MB.', file.size, 50)) return;

        onUpload({ files: sanitizedFiles, originalType, type: selectedType });
      } else {
        setUnsupportedType(true);
      }

      return;
    }

    if (type.includes(IMAGE)) {
      type = IMAGE;

      if (checkFileSize('Images should not exceed 5MB.', file.size, 5)) return;
    } else if (name.includes(DOCX)) {
      type = DOCS;
      originalType = DOCX;

      if (checkFileSize('Document files should not exceed 10MB.', file.size, 10)) return;
    } else if (type.includes(PDF)) {
      type = DOCS;
      originalType = PDF;

      if (checkFileSize('PDF files should not exceed 10MB.', file.size, 10)) return;
    } else {
      setUnsupportedType(true);
      return;
    }

    onUpload({ files: sanitizedFiles, originalType, type });
  };

  useEffect(() => {
    setUnsupportedType(false);

    if (confirmateUpload) return;

    if (files?.length > 0) {
      handleUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, selectedType, onUpload, confirmateUpload]);

  const handleFileChange = useCallback(
    (evt) => {
      setSizeError(null);

      const filteredFiles =
        selectedType === DCM
          ? [].filter.call(evt.target.files, (file) => {
              const parts = file.name.split('.');
              return parts.length === 1 || parts[parts.length - 1].toLowerCase() === DCM;
            })
          : evt.target.files;

      setFiles([...filteredFiles]);

      evt.target.value = null;
    },
    [selectedType]
  );

  const renderUploadSection = useCallback(() => {
    let accept = null;
    if (selectedType === DOCS_IMAGES) {
      accept = [...imageFormats, ...documentFormats].map((e) => `.${e}`).join(',');
      accept += allowDocxFiles ? ',.docx' : '';
    } else if (selectedType === VIDEO) {
      accept = videoFormats.map((e) => `.${e}`).join(',');
    }

    const isDisabled = !selectedType;

    let filename = '';

    if (files) {
      filename = files.length < 5 ? files.map((file) => file.name).join(', ') : `${files.length} files selected`;
    }

    return (
      <div className={classes.uploadFileContainer}>
        <input
          onChange={handleFileChange}
          multiple={selectedType === DCM ? '' : null}
          directory={selectedType === DCM ? '' : null}
          webkitdirectory={selectedType === DCM ? '' : null}
          moxdirectory={selectedType === DCM ? '' : null}
          accept={accept}
          disabled={isDisabled}
          hidden
          id="additional-file-upload-input"
          type="file"
        />

        {!isDisabled ? (
          <label htmlFor="additional-file-upload-input">
            <Button color="primary" variant="outlined" component="span">
              Choose File(s)
            </Button>
          </label>
        ) : (
          <Button disabled color="primary" variant="outlined" component="span">
            Choose File(s)
          </Button>
        )}

        {filename && confirmateUpload && !unsupportedType && <Typography className={classes.typographyFilename}>{filename}</Typography>}

        {unsupportedType && <Typography className={classes.typographyError}>Unsupported file type!</Typography>}
        {sizeError && <Typography className={classes.typographyError}>{sizeError}</Typography>}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, files, handleFileChange, selectedType, unsupportedType]);

  return (
    <Container disableGutters className={classes.container}>
      <img onClick={handleClose} src={IconClose} className={classes.iconClose} alt="Close Icon" />

      <Typography className={classes.typographyTitle}>{dialogTitle || 'Add Additional File'}</Typography>

      <EditFields ref={typeFieldsRef} fields={typeFields} labelPosition="top" labelClass={classes.fieldLabel} useAutoFocus={false} />

      {renderUploadSection()}

      {confirmateUpload && (
        <div className={classes.containerButton}>
          <LoadingButton onClick={handleClose} variant="outlined" className={classes.buttonCancel}>
            Cancel
          </LoadingButton>
          <LoadingButton onClick={handleUpload} disabled={!files?.length} variant="contained" color="primary">
            Save
          </LoadingButton>
        </div>
      )}
    </Container>
  );
};
